import {useTranslation} from 'react-i18next'

import {
  TaskDetailsCard,
  BaseTaskDetails,
  MissingTaskDataError,
  MetaAnnotation
} from '../../components'
import {FEATURE_NAMESPACE} from '../../consts'
import {HProduceTaskIdentifiers, HProduceTask, TaskSource, SideCardItemMode} from '../../types'

import {DalogActionDetailsContainer} from './DalogActionDetailsContainer'
import {MachineSentryActionDetailsContainer} from './MachineSentryActionDetailsContainer'
import {OilAnalysisActionDetailsContainer} from './OilAnalysisActionDetailsContainer'
import {PlantActionDetailsContainer} from './PlantActionDetailsContainer'
import {ProcessWorkOrderContainer} from './ProcessWorkOrderContainer'
import {ShiftTaskDetailsContainer} from './ShiftTaskDetailsContainer'

type Props = {
  // Task identifiers used for API calls (id, externalId, plantId, source)
  taskIdentifiers: HProduceTaskIdentifiers
  // Complete task object from the table/list - used for task sources without dedicated API endpoints
  taskFromList?: HProduceTask
  onClose: () => void
  onEditTaskClick: (hproduceTaskId: string, mode?: SideCardItemMode) => void
  timezone: string
  freeSearchText?: string
}

export const TaskDetailsFactory = ({
  taskIdentifiers,
  onClose,
  onEditTaskClick,
  freeSearchText,
  timezone,
  taskFromList
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  switch (taskIdentifiers.source) {
    case TaskSource.COCKPIT:
      return (
        <ShiftTaskDetailsContainer task={taskIdentifiers} onClose={onClose} timezone={timezone} />
      )
    case TaskSource.EASY_POM:
      return (
        <PlantActionDetailsContainer task={taskIdentifiers} onClose={onClose} timezone={timezone} />
      )
    case TaskSource.PROCESS_WORK_ORDER:
      return (
        <ProcessWorkOrderContainer
          task={taskIdentifiers}
          onClose={onClose}
          onEditTaskClick={onEditTaskClick}
          // Uses plant timezone
        />
      )
    case TaskSource.MACHINE_SENTRY_ACTIONS:
      return (
        <MachineSentryActionDetailsContainer
          key={taskIdentifiers.id}
          task={taskIdentifiers}
          onClose={onClose}
          onEditTaskClick={onEditTaskClick}
        />
      )
    case TaskSource.OIL_ANALYSIS_ACTIONS:
      return (
        <OilAnalysisActionDetailsContainer
          key={taskIdentifiers.id}
          task={taskIdentifiers}
          onClose={onClose}
          onEditTaskClick={onEditTaskClick}
        />
      )
    case TaskSource.DALOG_ACTIONS:
      return (
        <DalogActionDetailsContainer
          key={taskIdentifiers.id}
          task={taskIdentifiers}
          onClose={onClose}
          onEditTaskClick={onEditTaskClick}
        />
      )

    default: {
      if (!taskFromList) {
        return <MissingTaskDataError onClose={onClose} />
      }

      return (
        <TaskDetailsCard
          data-test-id="page-events-details"
          onClose={onClose}
          item={taskFromList}
          freeSearchText={freeSearchText}
          timezone={timezone}
          enableSourceAppInfo={false}
          metaAnnotationSlot={
            <MetaAnnotation
              label={t('metaAnnotation.label.created')}
              date={taskFromList.created}
              timezone={timezone}
              data-test-id="meta-annotation-created-on"
            />
          }
        >
          <BaseTaskDetails task={taskFromList} timezone={timezone} />
        </TaskDetailsCard>
      )
    }
  }
}
